import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { LogOut, Ruler, Package, ArrowRight, Scissors } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { fitProfile } = useShop();
  const navigate = useNavigate();
  const [measurements, setMeasurements] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/me/measurements").then((r) => setMeasurements(r.data)).catch(() => {});
      api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading || !user) {
    return <div className="pt-40 text-center text-charcoal/50" data-testid="account-loading">Loading…</div>;
  }

  const latestFit = measurements[0] || fitProfile;

  return (
    <div data-testid="account-page" className="pt-28 pb-32 min-h-screen">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-sunkissed mb-2">Your Atelier</div>
            <h1 className="font-serif text-4xl md:text-5xl font-light">Hello, {user.name || "there"}</h1>
            <p className="text-charcoal/55 mt-2">{user.email}</p>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
            className="inline-flex items-center gap-2 overline border border-charcoal/25 px-5 py-3 hover:border-charcoal transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>

        {user.role === "admin" && (
          <Link to="/admin" data-testid="account-admin-link" className="inline-flex items-center gap-3 bg-ocean text-sand px-6 py-3 overline mb-10 hover:opacity-90 transition-opacity">
            <Scissors size={15} /> Open Atelier Dashboard
          </Link>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Fit profile */}
          <div className="bg-white border border-charcoal/10 rounded-2xl p-7">
            <div className="flex items-center gap-2 overline mb-5"><Ruler size={16} /> Fit Profile</div>
            {latestFit ? (
              <div>
                <div className="font-serif text-2xl mb-1">Size {latestFit.recommended_size}</div>
                <p className="text-charcoal/55 text-sm mb-5">{latestFit.fit_notes}</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {Object.entries(latestFit.measurements_cm || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-charcoal/10 pb-1.5 text-sm">
                      <span className="text-charcoal/70 capitalize">{k.replace(/_/g, " ")}</span>
                      <span>{typeof v === "number" ? `${v} cm` : v}</span>
                    </div>
                  ))}
                </div>
                <Link to="/fit-studio" className="inline-flex items-center gap-1.5 text-sm text-sunkissed mt-5 hover:gap-2.5 transition-all" data-testid="account-redo-fit">
                  Update fit profile <ArrowRight size={13} />
                </Link>
              </div>
            ) : (
              <div>
                <p className="text-charcoal/55 text-sm mb-4">No fit profile yet. Create one to unlock made-to-measure pieces.</p>
                <Link to="/fit-studio" className="inline-flex items-center gap-3 bg-ocean text-sand px-6 py-3 overline hover:opacity-90 transition-opacity" data-testid="account-create-fit">
                  Start Fit Studio <ArrowRight size={15} />
                </Link>
              </div>
            )}
          </div>

          {/* Orders */}
          <div className="bg-white border border-charcoal/10 rounded-2xl p-7">
            <div className="flex items-center gap-2 overline mb-5"><Package size={16} /> Orders</div>
            {orders.length === 0 ? (
              <p className="text-charcoal/55 text-sm" data-testid="no-orders">No orders yet — your tailored pieces will appear here.</p>
            ) : (
              <div className="space-y-4" data-testid="orders-list">
                {orders.map((o) => (
                  <div key={o.order_id} className="border-b border-charcoal/10 pb-4" data-testid={`order-${o.order_id}`}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{o.order_id}</span>
                      <span className="uppercase text-[10px] tracking-wider bg-ocean text-sand px-2 py-0.5">{o.status}</span>
                    </div>
                    <div className="text-xs text-charcoal/55 mt-1">
                      {o.items?.map((i) => `${i.name} ×${i.quantity}`).join(", ")}
                    </div>
                    <div className="text-sm mt-1">{(o.currency || "chf").toUpperCase()} {Number(o.amount).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
